import React, { useEffect, useMemo } from 'react';
import { Box, FlexControlledForm, useDebounceState } from '@common-fe/common-fe';

import { NO_KEY } from '@/common/constants';
import { ADMIN_1_ID, NO_ACCESS_ID } from '@/modules/ExpressSetup/AddEditContactForm/hooks/useRoleFields.tsx';
import useContactStore, {
  ContactPayload,
} from '@/modules/ExpressSetup/AddEditContactForm/stores/useContact.store';

import { useRoleFields } from './hooks';
import { useAccessToSetupFields } from './hooks';
import { useContactFields } from './hooks';

export interface Props {
  isMainContactMode?: boolean;
  isEditMode?: boolean;
  currentContact?: ContactPayload;
  isAbandoned?: boolean;
  summaryMode?: boolean;
}

const AddEditContactForm: React.FC<Props> = ({
  isMainContactMode,
  isEditMode,
  currentContact,
  isAbandoned,
  summaryMode,
}) => {
  const showErrors = useContactStore((state) => state.showErrors);
  const handleSetState = useContactStore((state) => state.setState);
  const { setValues, values } = useDebounceState<Partial<ContactPayload>>();
  const { setValues: setRoleValues, values: roleValues } = useDebounceState<Partial<ContactPayload>>();
  const { setValues: setAccessToSetupValues, values: accessToSetupValues } =
    useDebounceState<Partial<ContactPayload>>();

  const fields = useContactFields(isEditMode, summaryMode, currentContact);
  const roleFiedls = useRoleFields(isMainContactMode, summaryMode, currentContact);
  const accessToSetupFields = useAccessToSetupFields(isEditMode, undefined, summaryMode, currentContact);

  const contactFormKey = useMemo(() => JSON.stringify(fields.map(f => f.value)), [fields]);
  const roleFormKey = useMemo(() => JSON.stringify(roleFiedls.map(f => f.value)), [roleFiedls]);
  const accessToSetupFormKey = useMemo(() => JSON.stringify(accessToSetupFields.map(f => f.value)), [accessToSetupFields]);

  useEffect(() => {
    if (values || roleValues || accessToSetupValues) {
      const updatedState = {
        ...values,
        ...roleValues,
        ...accessToSetupValues,
        isAccessToSetup:
          roleValues?.role === NO_ACCESS_ID && !accessToSetupValues?.isAccessToSetup
            ? NO_KEY
            : accessToSetupValues?.isAccessToSetup,
        isPrimary: !!isMainContactMode,
        id: currentContact?.id,
      };

      handleSetState(updatedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, isMainContactMode, roleValues, accessToSetupValues]);

  return (
    <>
      <Box data-testid="contact-form" background="canvas" round="container1Round" margin={{ bottom: 'l' }}>
        <FlexControlledForm
          key={contactFormKey}
          fields={fields}
          editMode={!isAbandoned && ((currentContact && isEditMode) || !currentContact)}
          isModalType
          isThinMode
          showError={showErrors}
          onChangeValues={setValues}
        />
      </Box>
      <Box data-testid="role-form" background="canvas" round="container1Round" margin={{ bottom: 'l' }}>
        <FlexControlledForm
          key={roleFormKey}
          fields={roleFiedls}
          editMode={!isAbandoned && ((currentContact && isEditMode) || !currentContact)}
          isModalType
          isThinMode
          showError={showErrors}
          onChangeValues={setRoleValues}
        />
      </Box>
      {(isMainContactMode || (!isMainContactMode && roleValues?.role === ADMIN_1_ID)) && (
        <Box background="canvas" round="container1Round" data-testid="access-setup-form">
          <FlexControlledForm
            key={accessToSetupFormKey}
            fields={accessToSetupFields}
            editMode={!isAbandoned && ((currentContact && isEditMode) || !currentContact)}
            isModalType
            isThinMode
            showError={showErrors}
            onChangeValues={setAccessToSetupValues}
          />
        </Box>
      )}
    </>
  );
};

export default AddEditContactForm;
